import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

interface MenuItem {
  label: string
  path?: string
  icon?: string
  children?: MenuItem[]
}

interface MenuProps {
  isCollapsed?: boolean
}

function Menu({ isCollapsed = false }: MenuProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())
  const [collapsedMenus, setCollapsedMenus] = useState<Set<string>>(new Set())

  const menuItems: MenuItem[] = [
    {
      label: 'ダッシュボード',
      path: '/dashboards',
      icon: 'bi-speedometer2',
    },
    {
      label: '登録情報',
      path: '/contract',
      icon: 'bi-file-earmark-text',
    },
    {
      label: '案件一覧',
      path: '/projects',
      icon: 'bi-briefcase',
    },
    {
      label: 'お問い合わせ',
      path: '/contact',
      icon: 'bi-envelope',
    },
    {
      label: 'ヘルプ',
      icon: 'bi-question-circle',
      children: [
        {
          label: 'よくあるご質問',
          path: '/help/faq',
        },
        {
          label: 'マニュアル',
          path: '/help/manual',
        },
      ],
    },
  ]

  const isActive = (path?: string) => {
    if (!path) return false
    return location.pathname === path
  }

  const handleMainClick = (item: MenuItem) => {
    if (item.path) {
      navigate(item.path)
    }
  }

  const hasActiveChild = (children?: MenuItem[]) => {
    if (!children) return false
    return children.some((child) => isActive(child.path))
  }

  useEffect(() => {
    if (isCollapsed) {
      setExpandedMenus(new Set())
    }
  }, [isCollapsed])

  useEffect(() => {
    if (isCollapsed) {
      return
    }

    setCollapsedMenus((prevCollapsed) => {
      const newCollapsed = new Set(prevCollapsed)
      let changed = false

      menuItems.forEach((item) => {
        if (item.children && !hasActiveChild(item.children) && newCollapsed.has(item.label)) {
          newCollapsed.delete(item.label)
          changed = true
        }
      })

      return changed ? newCollapsed : prevCollapsed
    })
  }, [location.pathname, isCollapsed])

  useEffect(() => {
    if (isCollapsed) {
      setExpandedMenus(new Set())
      return
    }

    setExpandedMenus((prevExpanded) => {
      const newExpanded = new Set<string>()

      menuItems.forEach((item) => {
        if (item.children && hasActiveChild(item.children) && !collapsedMenus.has(item.label)) {
          newExpanded.add(item.label)
        }
      })

      if (
        newExpanded.size === prevExpanded.size &&
        Array.from(newExpanded).every((label) => prevExpanded.has(label))
      ) {
        return prevExpanded
      }

      return newExpanded
    })
  }, [location.pathname, isCollapsed, collapsedMenus])

  const isOpen = (label: string) => {
    if (isCollapsed) {
      return expandedMenus.has(label)
    }

    const menuItem = menuItems.find((item) => item.label === label)
    if (!menuItem?.children) return false
    if (collapsedMenus.has(label)) return false
    if (hasActiveChild(menuItem.children)) return true
    return expandedMenus.has(label)
  }

  const toggleMenu = (label: string) => {
    const menuItem = menuItems.find((item) => item.label === label)
    const currentlyOpen = isOpen(label)

    if (isCollapsed) {
      setExpandedMenus((prev) => {
        const next = new Set(prev)
        if (next.has(label)) {
          next.delete(label)
        } else {
          next.clear()
          next.add(label)
        }
        return next
      })
      return
    }

    if (currentlyOpen) {
      if (menuItem?.children && hasActiveChild(menuItem.children)) {
        setCollapsedMenus((prev) => {
          const newSet = new Set(prev)
          newSet.add(label)
          return newSet
        })
      } else {
        setExpandedMenus((prev) => {
          const newSet = new Set(prev)
          newSet.delete(label)
          return newSet
        })
      }
    } else if (menuItem?.children && hasActiveChild(menuItem.children)) {
      setCollapsedMenus((prev) => {
        const newSet = new Set(prev)
        newSet.delete(label)
        return newSet
      })
    } else {
      setExpandedMenus((prev) => {
        const newSet = new Set(prev)
        newSet.add(label)
        return newSet
      })
    }
  }

  const handleSubMenuClick = (childPath: string) => {
    navigate(childPath)

    if (isCollapsed) {
      setExpandedMenus(new Set())
    }
  }

  return (
    <nav className={`menu ${isCollapsed ? 'menu-collapsed' : ''}`}>
      <ul className="menu-list">
        {menuItems.map((item) => (
          <li key={item.label} className="menu-item">
            {item.children ? (
              <>
                <button
                  className={`menu-button menu-button-parent ${isOpen(item.label) ? 'open' : ''}`}
                  onClick={() => toggleMenu(item.label)}
                >
                  {item.icon && <i className={`bi ${item.icon}`}></i>}
                  <span className="menu-label">{item.label}</span>
                  <span className="menu-arrow">›</span>
                </button>
                {isOpen(item.label) && (
                  <ul className="menu-sublist">
                    {item.children.map((child) => (
                      <li key={child.path} className="menu-subitem">
                        <button
                          className={`menu-button menu-button-child ${isActive(child.path) ? 'active' : ''}`}
                          onClick={() => child.path && handleSubMenuClick(child.path)}
                        >
                          <span className="menu-label">{child.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <button
                className={`menu-button ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleMainClick(item)}
              >
                {item.icon && <i className={`bi ${item.icon}`}></i>}
                <span className="menu-label">{item.label}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default Menu

